import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, XCircle, X, Eye } from "lucide-react";
import { TRADE_DEMOS } from "@/data/tradeDemos";
import { Logo } from "@/components/Logo";
import SiteMockup from "@/components/SiteMockup";
import Seo from "@/components/Seo";

const tiers = [
  { name: "Starter", setup: 299, monthly: 79, key: "starter" },
  { name: "Growth", setup: 499, monthly: 149, key: "growth", featured: true },
  { name: "Premium", setup: 899, monthly: 249, key: "premium" },
];

export default function TradeDemo() {
  const { slug } = useParams();
  const demo = TRADE_DEMOS[slug];
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPreview(null);
  }, [slug]);

  if (!demo) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <p className="font-serif text-3xl text-[#F8F6F0] mb-6">Trade not found.</p>
          <Link to="/" className="text-gold font-mono text-xs uppercase tracking-[0.2em]" data-testid="trade-not-found-back">← Back to all industries</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian grain" data-testid="trade-demo-page">
      <Seo
        title={`${demo.trade} Website Design`}
        description={`${demo.sub} See Starter, Growth and Premium website packages built for ${demo.trade.toLowerCase()} businesses.`}
      />
      <header className="border-b gold-border-subtle">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="trade-demo-logo-link">
            <Logo size={36} />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-lg tracking-[0.18em] text-[#F8F6F0]">TRADESITE</span>
              <span className="font-mono text-[9px] tracking-[0.52em] text-gold/80">PRO</span>
            </div>
          </Link>
          <Link to="/" data-testid="trade-demo-back-link" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-gold transition-colors">
            <ArrowLeft size={14} /> All Industries
          </Link>
        </div>
      </header>

      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={demo.image} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/85 to-obsidian" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-6"
            data-testid="trade-demo-eyebrow"
          >
            Interactive Website Showcase · {demo.trade}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.08] tracking-tight text-[#F8F6F0] max-w-4xl"
            data-testid="trade-demo-headline"
          >
            {demo.hero}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-6 max-w-xl text-zinc-400 leading-relaxed"
          >
            {demo.sub}
          </motion.p>
        </div>
      </section>

      <section className="py-20 border-t gold-border-subtle">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-serif text-3xl text-[#F8F6F0] mb-8">Sound familiar?</h2>
            <ul className="space-y-5">
              {demo.painPoints.map((p) => (
                <li key={p} className="flex items-start gap-4">
                  <XCircle size={20} className="text-red-400/70 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="text-zinc-400 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <h2 className="font-serif text-3xl text-[#F8F6F0] mb-8">What your site would include</h2>
            <ul className="space-y-5">
              {demo.features.map((f) => (
                <li key={f} className="flex items-start gap-4">
                  <Check size={20} className="text-gold mt-0.5 shrink-0" strokeWidth={2} />
                  <span className="text-zinc-300 leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-20 border-t gold-border-subtle bg-surface/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F8F6F0] mb-3">Three ways to start.</h2>
          <p className="text-zinc-500 text-sm mb-12">
            Tap <span className="text-gold">View example</span> on any package to preview the {demo.trade.toLowerCase()} website you'd get.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {tiers.map((t, i) => (
              <motion.div
                key={t.name}
                data-testid={`trade-tier-${t.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`relative rounded-3xl p-8 flex flex-col ${
                  t.featured ? "trace-border border gold-border-subtle bg-elevated" : "border border-zinc-800 bg-surface/60"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3.5 left-8 px-4 py-1.5 rounded-full bg-gold text-obsidian font-mono text-[10px] uppercase tracking-[0.2em] font-medium">
                    Most Chosen
                  </span>
                )}
                <h3 className="font-serif text-2xl text-[#F8F6F0]">{t.name}</h3>
                <div className="mt-4 flex items-end gap-2">
                  <span className={`font-serif text-4xl ${t.featured ? "gold-text" : "text-[#F8F6F0]"}`}>${t.setup}</span>
                  <span className="text-zinc-500 text-sm pb-1">setup + ${t.monthly}/mo</span>
                </div>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{demo.packages[t.key]}</p>
                <button
                  data-testid={`trade-preview-${t.name.toLowerCase()}`}
                  onClick={() => setPreview(t.key)}
                  className="mt-5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-gold hover:text-gold-light transition-colors"
                >
                  <Eye size={14} /> View example site
                </button>
                <Link
                  to="/#contact"
                  data-testid={`trade-tier-cta-${t.name.toLowerCase()}`}
                  className={`mt-7 flex items-center justify-center gap-2 py-3.5 rounded-full font-sans font-semibold text-sm transition-all duration-300 ${
                    t.featured ? "bg-gold text-obsidian hover:bg-gold-hover" : "border border-zinc-700 text-zinc-200 hover:border-gold/60 hover:text-gold"
                  }`}
                >
                  Start with {t.name} <ArrowRight size={15} />
                </Link>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            Demonstration concepts for {demo.trade.toLowerCase()} — fictional business, no quote requests are transmitted from this page.
          </p>
        </div>
      </section>

      <AnimatePresence>
        {preview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-obsidian/85 backdrop-blur-md"
            onClick={() => setPreview(null)}
            data-testid="trade-preview-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 24 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="trade-preview-modal"
              className="trace-border relative w-full max-w-2xl rounded-3xl border gold-border-subtle bg-surface overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b gold-border-subtle">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{demo.demoBusiness} · {preview} package</span>
                </div>
                <div className="flex items-center gap-2">
                  {tiers.map((t) => (
                    <button
                      key={t.key}
                      data-testid={`preview-tab-${t.key}`}
                      onClick={() => setPreview(t.key)}
                      className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-wider transition-colors ${
                        preview === t.key ? "bg-gold text-obsidian" : "text-zinc-400 hover:text-gold border border-zinc-800"
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                  <button data-testid="trade-preview-close" onClick={() => setPreview(null)} className="ml-2 text-zinc-500 hover:text-gold transition-colors">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <SiteMockup demo={demo} tier={preview} />
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                  Interactive concept preview — the real build is customized to your business.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
